/**
 * formatCents.ts — Shared currency formatting utility for credit displays.
 *
 * Supports two precision modes:
 *   - 'cents' (default): Rounds to 2 decimal places (e.g. $99.88)
 *   - 'precision': Shows 5 decimal places (e.g. $99.87985)
 *
 * The precision mode is stored in localStorage and can be toggled via the UI.
 */

const STORAGE_KEY = 'r42-billing-precision-mode';
const PRECISION_DECIMALS = 5;
const CENTS_DECIMALS = 2;

export type PrecisionMode = 'cents' | 'precision';

/** Read the current precision mode from localStorage. Defaults to 'cents'. */
export function getPrecisionMode(): PrecisionMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'precision' ? 'precision' : 'cents';
  } catch {
    return 'cents';
  }
}

/** Save the precision mode to localStorage. */
export function setPrecisionMode(mode: PrecisionMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Format a cents value as a dollar string.
 *
 * @param cents - The amount in cents (may be fractional, e.g. 9787.9845)
 * @param mode - Optional precision mode override. If not provided, reads from localStorage.
 * @returns Formatted dollar string (e.g. "$97.88" or "$97.87985")
 */
export function formatCents(cents: number, mode?: PrecisionMode): string {
  const precision = mode ?? getPrecisionMode();
  const decimals = precision === 'precision' ? PRECISION_DECIMALS : CENTS_DECIMALS;
  const dollars = cents / 100;
  return `$${dollars.toFixed(decimals)}`;
}

/**
 * Format a cents value as a dollar string with explicit sign.
 *
 * @param cents - The amount in cents (may be fractional)
 * @param direction - 'credit' (positive) or 'debit' (negative)
 * @param mode - Optional precision mode override.
 * @returns Formatted string with sign (e.g. "+$5.00" or "-$2.50")
 */
export function formatCentsSigned(cents: number, direction: string, mode?: PrecisionMode): string {
  const formatted = formatCents(Math.abs(cents), mode);
  return direction === 'credit' ? `+${formatted}` : `-${formatted}`;
}
